import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import Service from '../../../services';
import Authentication from '../../session';
import withRouter from '../../lib/withRouter';
import { baseUrlAdmin } from '../../../store/apiConstants';
import LoadingComponent from '../../loadingComponent';
import './styles.css';

class GradingScheme extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            gradingSchemes: [],
            showDialog: false,
            isEditMode: false,
            currentScheme: null,
            formData: {
                name: '',
                grades: [
                    { from: 0, to: 35, grade: 'F', gradePoints: 0, gradeName: 'Fail', result: 'Fail' }
                ]
            },
            errors: {}
        };
        this.service = new Service();
        this.toast = React.createRef();
        this.resultOptions = [
            { label: 'Pass', value: 'Pass' },
            { label: 'Fail', value: 'Fail' }
        ];
    }

    componentDidMount() {
        this.fetchGradingSchemes();
    }

    fetchGradingSchemes = async () => {
        this.setState({ isLoading: true });
        try {
            const url = `${baseUrlAdmin}/grading-schemes`;
            const response = await this.service.get(url, true);

            if (response && response.status) {
                this.setState({
                    gradingSchemes: response.res.data || [],
                    isLoading: false
                });
            } else {
                this.showToast('error', 'Error', response?.errMessage || 'Failed to fetch grading schemes');
                this.setState({ isLoading: false });
            }
        } catch (error) {
            console.error('Error fetching grading schemes:', error);
            this.showToast('error', 'Error', 'Failed to fetch grading schemes');
            this.setState({ isLoading: false });
        }
    };

    showToast = (severity, summary, detail) => {
        this.toast.current.show({ severity, summary, detail, life: 3000 });
    };

    openCreateDialog = () => {
        this.setState({
            showDialog: true,
            isEditMode: false,
            currentScheme: null,
            formData: {
                name: '',
                grades: [
                    { from: 0, to: 35, grade: 'F', gradePoints: 0, gradeName: 'Fail', result: 'Fail' }
                ]
            },
            errors: {}
        });
    };

    openEditDialog = (scheme) => {
        this.setState({
            showDialog: true,
            isEditMode: true,
            currentScheme: scheme,
            formData: {
                name: scheme.name,
                grades: scheme.grades.map(grade => ({ ...grade }))
            },
            errors: {}
        });
    };

    closeDialog = () => {
        this.setState({
            showDialog: false,
            isEditMode: false,
            currentScheme: null,
            formData: {
                name: '',
                grades: [
                    { from: 0, to: 35, grade: 'F', gradePoints: 0, gradeName: 'Fail', result: 'Fail' }
                ]
            },
            errors: {}
        });
    };

    handleNameChange = (e) => {
        this.setState({
            formData: {
                ...this.state.formData,
                name: e.target.value
            },
            errors: {
                ...this.state.errors,
                name: ''
            }
        });
    };

    addGradeRow = () => {
        const { formData } = this.state;
        const lastGrade = formData.grades[formData.grades.length - 1];
        const newFrom = lastGrade.to; // Set equal to previous 'to' for contiguous ranges

        this.setState({
            formData: {
                ...formData,
                grades: [
                    ...formData.grades,
                    {
                        from: newFrom,
                        to: newFrom + 10,
                        grade: '',
                        gradePoints: 0,
                        gradeName: '',
                        result: 'Pass'
                    }
                ]
            }
        });
    };

    removeGradeRow = (index) => {
        const { formData } = this.state;
        if (formData.grades.length > 1) {
            const newGrades = formData.grades.filter((_, i) => i !== index);
            this.setState({
                formData: {
                    ...formData,
                    grades: newGrades
                }
            });
        }
    };

    updateGradeRow = (index, field, value) => {
        const { formData } = this.state;
        const newGrades = [...formData.grades];

        // Handle numeric fields
        if (field === 'from' || field === 'to' || field === 'gradePoints') {
            // If value is empty string, keep it as empty string (user is clearing the field)
            // Otherwise convert to number
            newGrades[index][field] = value === '' ? '' : Number(value);
        } else {
            newGrades[index][field] = value;
        }

        this.setState({
            formData: {
                ...formData,
                grades: newGrades
            },
            errors: {
                ...this.state.errors,
                [`grade_${index}`]: ''
            }
        });
    };

    validateForm = () => {
        const { formData } = this.state;
        const errors = {};

        // Validate name
        if (!formData.name || !formData.name.trim()) {
            errors.name = 'Grading scheme name is required';
        }

        // Validate grades
        if (formData.grades.length === 0) {
            errors.grades = 'At least one grade range is required';
        }

        formData.grades.forEach((grade, index) => {
            // Check if required fields are missing or empty
            const fromValue = grade.from === '' || grade.from === null || grade.from === undefined;
            const toValue = grade.to === '' || grade.to === null || grade.to === undefined;
            const gradePointsValue = grade.gradePoints === '' || grade.gradePoints === null || grade.gradePoints === undefined;

            if (!grade.grade || !grade.gradeName || fromValue || toValue || gradePointsValue) {
                errors[`grade_${index}`] = 'All fields are required';
            }

            // Only validate from < to if both have valid numeric values
            if (!fromValue && !toValue && grade.from >= grade.to) {
                errors[`grade_${index}`] = 'From must be less than To';
            }
        });

        // Check for contiguous ranges
        const sortedGrades = [...formData.grades].sort((a, b) => a.from - b.from);
        if (sortedGrades.length > 0) {
            if (sortedGrades[0].from !== 0) {
                errors.grades = 'First grade range must start from 0';
            }

            if (sortedGrades[sortedGrades.length - 1].to !== 100) {
                errors.grades = 'Last grade range must end at 100';
            }

            // Check for contiguous ranges - each range's 'from' should equal previous range's 'to'
            for (let i = 1; i < sortedGrades.length; i++) {
                if (sortedGrades[i].from !== sortedGrades[i - 1].to) {
                    errors.grades = `Grade ranges must be contiguous. Range ${i + 1}'s "From" (${sortedGrades[i].from}) must equal Range ${i}'s "To" (${sortedGrades[i - 1].to})`;
                    break;
                }
            }
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    handleSubmit = async () => {
        if (!this.validateForm()) {
            this.showToast('error', 'Validation Error', 'Please fix the errors in the form');
            return;
        }

        this.setState({ isLoading: true });
        try {
            const { isEditMode, currentScheme, formData } = this.state;

            let response;
            if (isEditMode) {
                const url = `${baseUrlAdmin}/grading-schemes/${currentScheme.gradingSchemeId}`;
                response = await this.service.put(url, formData, true);
            } else {
                const url = `${baseUrlAdmin}/grading-schemes`;
                response = await this.service.post(url, formData, true);
            }

            if (response && response.status) {
                this.showToast(
                    'success',
                    'Success',
                    `Grading scheme ${isEditMode ? 'updated' : 'created'} successfully`
                );
                this.closeDialog();
                this.fetchGradingSchemes();
            } else {
                this.showToast('error', 'Error', response?.errMessage || response?.res?.message || 'Operation failed');
                this.setState({ isLoading: false });
            }
        } catch (error) {
            console.error('Error submitting grading scheme:', error);
            this.showToast('error', 'Error', 'Failed to save grading scheme');
            this.setState({ isLoading: false });
        }
    };

    renderActions = (rowData) => {
        return (
            <div>
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-success p-button-text action-button"
                    tooltip="Edit"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => this.openEditDialog(rowData)}
                />
            </div>
        );
    };

    renderGradeRows = () => {
        const { formData, errors } = this.state;

        return (
            <div className="grade-rows-container">
                <div style={{ marginBottom: '1rem', fontWeight: 600 }}>Grade Ranges:</div>

                <div style={{
                    marginBottom: '1rem',
                    padding: '10px',
                    backgroundColor: '#f0f8ff',
                    borderLeft: '4px solid #2196F3',
                    fontSize: '0.9em'
                }}>
                    <strong>How grade matching works:</strong>
                    <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                        <li><strong>First range:</strong> Percentage ≥ From AND Percentage ≤ To (e.g., 0 ≤ percentage ≤ 35)</li>
                        <li><strong>Other ranges:</strong> Percentage &gt; From AND Percentage ≤ To (e.g., 35 &lt; percentage ≤ 50)</li>
                    </ul>
                    <em>Tip: Ranges must be contiguous. Set the next range's "From" equal to the previous range's "To".</em>
                </div>

                {errors.grades && (
                    <div className="validation-error" style={{ marginBottom: '1rem' }}>{errors.grades}</div>
                )}

                {formData.grades.map((grade, index) => (
                    <div key={index} className="grade-row-item">
                        <div style={{ minWidth: '30px', fontWeight: 600 }}>{index + 1}.</div>

                        <div className="grade-input-wrapper">
                            <label className="grade-field-label">
                                From ({index === 0 ? '≥' : '>'} %)
                                <span className="form-field-required">*</span>
                                <i
                                    className="pi pi-info-circle"
                                    style={{ marginLeft: '5px', fontSize: '0.9em', cursor: 'help' }}
                                    title={index === 0
                                        ? "First range: Percentage must be greater than OR equal to this value (≥)"
                                        : "Percentage must be greater than this value (>)"}
                                />
                            </label>
                            <InputText
                                type="number"
                                value={grade.from === '' ? '' : grade.from}
                                onChange={(e) => this.updateGradeRow(index, 'from', e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                placeholder="From %"
                                min="0"
                                max="100"
                            />
                        </div>

                        <div className="grade-input-wrapper">
                            <label className="grade-field-label">
                                To (≤ %)
                                <span className="form-field-required">*</span>
                                <i
                                    className="pi pi-info-circle"
                                    style={{ marginLeft: '5px', fontSize: '0.9em', cursor: 'help' }}
                                    title="Percentage must be less than or equal to this value (≤)"
                                />
                            </label>
                            <InputText
                                type="number"
                                value={grade.to === '' ? '' : grade.to}
                                onChange={(e) => this.updateGradeRow(index, 'to', e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                placeholder="To %"
                                min="0"
                                max="100"
                            />
                        </div>

                        <div className="grade-input-wrapper">
                            <label className="grade-field-label">Grade<span className="form-field-required">*</span></label>
                            <InputText
                                value={grade.grade}
                                onChange={(e) => this.updateGradeRow(index, 'grade', e.target.value)}
                                placeholder="Grade"
                            />
                        </div>

                        <div className="grade-input-wrapper">
                            <label className="grade-field-label">Grade Points<span className="form-field-required">*</span></label>
                            <InputText
                                type="number"
                                value={grade.gradePoints === '' ? '' : grade.gradePoints}
                                onChange={(e) => this.updateGradeRow(index, 'gradePoints', e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                placeholder="Points"
                                step="0.1"
                            />
                        </div>

                        <div className="grade-input-wrapper">
                            <label className="grade-field-label">Grade Name<span className="form-field-required">*</span></label>
                            <InputText
                                value={grade.gradeName}
                                onChange={(e) => this.updateGradeRow(index, 'gradeName', e.target.value)}
                                placeholder="Grade Name"
                            />
                        </div>

                        <div className="grade-input-wrapper">
                            <label className="grade-field-label">Result<span className="form-field-required">*</span></label>
                            <Dropdown
                                value={grade.result}
                                options={this.resultOptions}
                                onChange={(e) => this.updateGradeRow(index, 'result', e.value)}
                                placeholder="Select Result"
                            />
                        </div>

                        <div className="grade-row-actions" style={{ alignSelf: 'flex-end' }}>
                            {formData.grades.length > 1 && (
                                <Button
                                    icon="pi pi-trash"
                                    className="p-button-rounded p-button-danger p-button-text"
                                    onClick={() => this.removeGradeRow(index)}
                                    tooltip="Delete"
                                    tooltipOptions={{ position: 'top' }}
                                />
                            )}
                        </div>

                        {errors[`grade_${index}`] && (
                            <div className="validation-error" style={{ gridColumn: '1 / -1' }}>
                                {errors[`grade_${index}`]}
                            </div>
                        )}
                    </div>
                ))}

                <Button
                    label="Add Grade Range"
                    icon="pi pi-plus"
                    className="p-button-outlined add-grade-button"
                    onClick={this.addGradeRow}
                />
            </div>
        );
    };

    render() {
        const { isLoading, gradingSchemes, showDialog, isEditMode, formData, errors } = this.state;

        const dialogFooter = (
            <div className="dialog-footer">
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    className="p-button-text"
                    onClick={this.closeDialog}
                    disabled={isLoading}
                />
                <Button
                    label={isEditMode ? 'Update' : 'Create'}
                    icon="pi pi-check"
                    onClick={this.handleSubmit}
                    disabled={isLoading}
                />
            </div>
        );

        return (
            <div className="grading-scheme-container">
                <Toast ref={this.toast} />
                {isLoading && !showDialog && <LoadingComponent />}

                <div className="grading-scheme-header">
                    <h2>Grading Scheme</h2>
                    <Button
                        label="Create Grading Scheme"
                        icon="pi pi-plus"
                        className="create-button"
                        onClick={this.openCreateDialog}
                    />
                </div>

                <Card className="grading-table-card">
                    <DataTable
                        value={gradingSchemes}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        emptyMessage="No grading schemes found"
                        className="ma-scroll-vr-smoke"
                    >
                        <Column
                            header="SNO"
                            body={(data, options) => options.rowIndex + 1}
                            style={{ width: '80px' }}
                        />
                        <Column
                            field="name"
                            header="Grading Scheme Name"
                            sortable
                        />
                        <Column
                            header="Grade Ranges"
                            body={(rowData) => `${rowData.grades?.length || 0} ranges`}
                        />
                        <Column
                            header="Created"
                            body={(rowData) => new Date(rowData.createdAt).toLocaleDateString()}
                            sortable
                        />
                        <Column
                            header="Actions"
                            body={this.renderActions}
                            style={{ width: '150px' }}
                        />
                    </DataTable>
                </Card>

                <Dialog
                    header={isEditMode ? 'Edit Grading Scheme' : 'Create Grading Scheme'}
                    visible={showDialog}
                    style={{ width: '90vw', maxWidth: '1200px' }}
                    onHide={this.closeDialog}
                    footer={dialogFooter}
                    modal
                >
                    <div>
                        <div className="p-field" style={{ marginBottom: '1.5rem' }}>
                            <label htmlFor="schemeName">
                                Grading Scheme Name<span className="form-field-required">*</span>
                            </label>
                            <InputText
                                id="schemeName"
                                value={formData.name}
                                onChange={this.handleNameChange}
                                placeholder="Enter grading scheme name"
                                style={{ width: '100%', marginTop: '0.5rem' }}
                                className={errors.name ? 'p-invalid' : ''}
                            />
                            {errors.name && (
                                <small className="validation-error">{errors.name}</small>
                            )}
                        </div>

                        {this.renderGradeRows()}
                    </div>
                </Dialog>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.user
});

export default connect(mapStateToProps)(Authentication(withRouter(GradingScheme)));
